import React, { useEffect, useMemo, useState } from "react";

import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { useDeviceContext } from "../../../../../context/DeviceContext";
import { WebViewPopover } from "../../SubHeader/WebViewPopover";
import { Input } from "../../../../ui/input";
import { ChevronDown, Search } from "lucide-react";
import { useUrlNavigation } from "../../../../../hooks/useUrlSearchParams";

type VenueSelectorProps = {
  dir?: "rtl" | "ltr";
};

export const VenueSelectorEDS: React.FC<VenueSelectorProps> = () => {
  const { updateUrl } = useUrlNavigation();

  const [isPopoverOpen, setIsPopoverOpen] = useState<boolean>(false);
  const [searchCriteria, setSearchCriteria] = useState<string>("");
  const [venueData, setVenueData] = useState<
    {
      label: string;
      value: any;
    }[]
  >([]);

  const { selectedVenue, selectedView, date, venues } = useDiaryContext();
  const { connectionStatus } = useDeviceContext();

  const filteredVenues = useMemo(() => {
    if (!searchCriteria) return venueData;
    return venueData.filter((venue) =>
      venue.label.toLowerCase().includes(searchCriteria.toLowerCase())
    );
  }, [venueData, searchCriteria]);

  useEffect(() => {
    if (!venues) {
      return;
    }

    setVenueData(
      venues?.map((venue) => {
        return {
          label: `${venue.title}`,
          value: venue._id
        };
      }) ?? []
    );
  }, [venues, selectedVenue]);

  const handleValueChanged = (value: string) => {
    setIsPopoverOpen(false);
    if (value === "" || value === selectedVenue) {
      return;
    }

    if (selectedView && date) {
      updateUrl({
        venueId: value,
        tab: selectedView,
        date: date
      });
    }
  };

  const selectedVenueLabel = useMemo(() => {
    return (
      venueData.find((venue) => venue.value === selectedVenue)?.label ||
      "Select a site"
    );
  }, [venueData, selectedVenue]);

  return (
    <div className="VenueSelector-eds mr-3">
      <WebViewPopover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        side="bottom"
        sideOffset={8}
        content={
          <>
            <div className="p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search Venues"
                  value={searchCriteria}
                  onChange={(e) => setSearchCriteria(e.target.value)}
                  className="pl-10"
                  autoFocus
                />
              </div>
            </div>
            <div className="max-h-[300px] overflow-y-auto px-2 pb-2">
              {filteredVenues.length > 0 ? (
                filteredVenues.map((venue) => {
                  const isSelected = venue.value === selectedVenue;
                  return (
                    <button
                      key={venue.value}
                      className={`w-full px-3 py-2 text-left text-sm focus:outline-none rounded-md ${
                        isSelected
                          ? "bg-accent text-accent-foreground font-medium"
                          : "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                      }`}
                      onClick={() => handleValueChanged(venue.value)}
                    >
                      {venue.label}
                    </button>
                  );
                })
              ) : (
                <div className="px-3 py-2 text-sm text-muted-foreground">
                  No venues found
                </div>
              )}
            </div>
          </>
        }
      >
        <button
          className="eds-button flex items-center justify-between w-full min-w-[200px] max-w-[300px] px-3 py-2 text-sm bg-background border border-input rounded-md hover:bg-accent hover:text-accent-foreground focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
          disabled={connectionStatus !== "internet"}
        >
          <span className="truncate text-left font-medium">
            {selectedVenueLabel}
          </span>
          <ChevronDown className="h-4 w-4 opacity-50 flex-shrink-0" />
        </button>
      </WebViewPopover>
    </div>
  );
};
